


































































































































































import Component, {mixins} from 'vue-class-component'
import { State, Action, Mutation } from 'vuex-class'
import {Post, PostPagination, PostPaginationFilters} from '@/modules/blog/types'
import * as moment from 'moment'
import { Watch } from 'vue-property-decorator'
import PostDeletionConfirm from '@/modules/blog/components/PostDeletionConfirm.vue'
import { debounce } from 'typescript-debounce-decorator'
import {VDataTableOptions} from '@/types'
import Loader from '@/components/Loader.vue';
import Authorizable from '@/modules/authorization/mixins/Authorizable'
import { User } from '@/modules/auth/types'
import _ from 'lodash';
import {DateMixin} from '@/modules/core/date';


/**
 * Lista de publicaciones
 *
 * Muestra al usuario una lista de artículos del blog y las opciones para
 * filtrar.
 *
 * @author Alejandro G. Riaño <agutierrez@softcontrol.es>
 */
@Component({
    components: {
        Loader,
        'post-deletion-confirm': PostDeletionConfirm,
    },
})
export default class PostListComponent extends mixins(Authorizable, DateMixin) {
    /**
     * Paginación de publicaciones actualmente mostrada al usuario.
     */
    @State(state => state.blog.currentPostPagination)
    public pagination: PostPagination

    /**
     * Indica si la aplicación esta cargando una nueva paginación de publicaciones.
     * Mostrará un cargador y deshabilitará botones
     */
    @State(state => state.blog.postPaginationLoading)
    public loading: boolean

    @State(state => state.blog.currentPostPaginationFilters)
    public filters: PostPaginationFilters

    @State(state => state.auth.user)
    public user: User

    @Mutation('showLoadingScreen')
    public showLoadingScreen: (payload: {message: string}) => void

    @Mutation('hideLoadingScreen')
    public hideLoadingScreen: () => void

    @Mutation('blog/showPostDeletionConfirm')
    public showDeletionConfirm: (show: boolean) => void

    @Mutation('blog/setPostToDelete')
    public setPostToDelete: (post: Post) => void

    /**
     * Actualiza los filtros de paginación
     *
     * @param filter { PostPaginationFilters }
     */
    @Mutation('blog/MERGE_POST_PAGINATION_FILTERS')
    public mergeFilters: (filter: Partial<PostPaginationFilters>) => void

    /**
     * Solicita a la aplicación que cargue una paginación de publicaciones
     */
    @Action('blog/loadPostPagination')
    public loadPagination: (filters?: PostPaginationFilters) => PostPagination

    public firstLoading = true
    public tableOptions: Partial<VDataTableOptions> = {}
    public mode = 0

    /**
     * Observa el modo de visualización de la pantalla y cuando esta cambia
     * guardar el estado en el localStorage
     */
    @Watch('mode')
    public watchMode (mode: number) {
        localStorage.setItem('blog.posts.displayMode', JSON.stringify(mode))
    }

    public headers = [
        { text: 'Id', value: 'id', sortable: false},
        { text: 'Titulo (Español)', value: 'title',  sortable: false},
        { text: 'Autor', value: 'author',  sortable: false},
        { text: 'Creación', value: 'createdAt', sortable: false},
        { text: 'Última actualización', value: 'updatedAt', sortable: false},
        { text: 'Traducciones', value: 'translations', sortable: false},
        { text: 'Acciones', value: 'action', sortable: false, align: 'center'},
    ]

    public get toolbarClasses () {
        return {
            'border-bottom-grey': !this.$vuetify.theme.dark,
            'elevation-2': this.$vuetify.theme.dark,
            'ma-0': true,
            'pr-5': true,
            'pl-5': true,
        }
    }

    /**
     * Actualiza la paginación
     *
     * Observa la propiedad "tablePagination", cuando se produce un cambio ejecuta
     * una acción de vuex para obtener una nueva paginación
     */
    @Watch('tableOptions', {deep: true})
    public async onPaginationChanged (value: VDataTableOptions, old: VDataTableOptions) {
        // Actualiza los filtros almacenados en el estado
        this.mergeFilters({
            recordsPerPage: value.itemsPerPage,
            page: value.page,
        })

        await this.loadPagination()
        this.firstLoading = false
    }

    @Watch('filters', {immediate: true})
    public watchFilters (filter: PostPaginationFilters) {
        this.tableOptions.itemsPerPage = filter.recordsPerPage
    }

    public onSearchTextChange = _.debounce(async function (this: PostListComponent, text: string) {
        this.mergeFilters({
            contains: { text, inLocale: '' },
        })

        await this.loadPagination()
    }, 800)


    public deletePost (post: Post): void {
        this.setPostToDelete(post)
        this.showDeletionConfirm(true)
    }

    public editPost (post: Post) {
        this.$router.push({
            name: 'blog.post',
            params: {
                id: post.id.toString(),
            },
        })
    }

    public newPost () {
        this.$router.push({
            name: 'blog.post',
            params: {
                id: 'nueva',
            },
        })
    }

    public async created () {
        await this.authorize([
            '@/blog/admin/posts/update',
            '@/blog/admin/posts/update_any',
            '@/blog/admin/posts/delete',
            '@/blog/admin/posts/delete_any',
            '@/blog/admin/posts/create',
        ])

        this.mode = JSON.parse(localStorage.getItem('blog.posts.displayMode') || '0')
    }

    /**
     * Indica si el usuario actual tiene la capacidad de crear
     * publicaciones
     */
    public get allowCreatePost (): boolean {
        return this.hasPermission('@/blog/admin/posts/create')
    }

    /**
     * Indica si el usuario actual tiene la capacidad de eliminar un determinado
     * post.
     *
     * @param post
     */
    public canDelete (post: Post): boolean {
        if (this.hasPermission('@/blog/admin/posts/delete_any')) {
            return true
        }

        if (!this.hasPermission('@/blog/admin/posts/delete')) {
            return false
        }

        // TODO: Eliminar en la versión 3.0 del blog
        if (!post.author) {
            return true
        }

        return this.user.id === post.author.id;
    }

    /**
     * Indica si el usuario actual tiene la capacidad para actualizar una determinada
     * publicación
     *
     * @param post
     */
    public canUpdate (post: Post): boolean {
        if (this.hasPermission('@/blog/admin/posts/update_any')) {
            return true
        }

        if (!this.hasPermission('@/blog/admin/posts/update')) {
            return false
        }

        // TODO: Eliminar en la versión 3.0 del blog
        if (!post.author) {
            return true
        }

        return this.user.id === post.author.id;
    }
}
